'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { askInputDraftInteractionEvent } from '#pplx/frontend/apps/ask/ask-input/ask-input-drafts/analytics.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
const DRAFT_PREVIEW_MAX_CHARS = 80;
function truncate(text, maxChars) {
    if (text.length <= maxChars)
        return text;
    return `${text.slice(0, maxChars).trimEnd()}…`;
}
export function AskInputDraftsControl({ drafts, deleteDraft, clearDrafts, onSelectDraft, onCloseAutoFocus, surface = 'thread', }) {
    const { $t } = useIntl();
    const { track } = useInstrumentation();
    const [open, setOpen] = useState(false);
    const handleOpenChange = useCallback((nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
            track(askInputDraftInteractionEvent({
                name: 'query drafts opened',
                data: { surface, draftCount: drafts?.length ?? 0 },
            }));
        }
    }, [drafts?.length, surface, track]);
    const draftCount = drafts?.length ?? 0;
    const triggerLabel = $t({
        defaultMessage: '{count, plural, one {# draft} other {# drafts}}',
        id: 'f377b3c599',
        description: 'Button to open saved Ask input drafts, including the number of drafts',
    }, { count: draftCount });
    const handleDelete = useCallback(async (draft) => {
        try {
            await deleteDraft(draft.uuid);
            track(askInputDraftInteractionEvent({
                name: 'query draft deleted',
                data: { surface, draftContext: draft.context.kind },
            }));
        }
        catch (error) {
            logger.warn('Unable to delete Ask input draft', { error });
        }
    }, [deleteDraft, surface, track]);
    const handleClear = useCallback(async () => {
        const draftCount = drafts?.length ?? 0;
        if (draftCount === 0)
            return;
        try {
            const didClearDrafts = await clearDrafts();
            if (!didClearDrafts)
                return;
            setOpen(false);
            track(askInputDraftInteractionEvent({
                name: 'query drafts cleared',
                data: { surface, draftContext: 'thread-followup', draftCount },
            }));
        }
        catch (error) {
            logger.warn('Unable to clear Ask input drafts', { error });
        }
    }, [clearDrafts, drafts?.length, surface, track]);
    const handleSelect = useCallback(async (draft) => {
        try {
            await onSelectDraft(draft);
            setOpen(false);
            track(askInputDraftInteractionEvent({
                name: 'query draft loaded',
                data: { surface, draftContext: draft.context.kind },
            }));
        }
        catch (error) {
            logger.warn('Unable to load Ask input draft', { error });
        }
    }, [onSelectDraft, surface, track]);
    const handleSelectUuid = useCallback((uuid) => {
        const draft = drafts?.find((candidate) => candidate.uuid === uuid);
        if (draft)
            void handleSelect(draft);
    }, [drafts, handleSelect]);
    if (drafts === undefined || draftCount === 0)
        return null;
    return (_jsxs(DropdownMenu, { isOpen: open, onToggle: handleOpenChange, onCloseAutoFocus: onCloseAutoFocus, align: "end", minWidthPx: 300, maxWidthPx: 360, maxHeightPx: 320, triggerElement: _jsx(Tooltip, { content: triggerLabel, children: _jsx(Button, { variant: "tonal", size: "tiny", pill: true, "aria-label": triggerLabel, children: _jsx(TextPrimitive, { variant: "tinyRegular", color: "light", children: triggerLabel }) }) }), children: [_jsx(DropdownMenu.RadioGroup, { value: "", onValueChange: handleSelectUuid, closeOnSelect: true, children: drafts.map((draft) => (_jsx(DropdownMenu.RadioItem, { value: draft.uuid, textValue: draft.snapshot.text, trailingAccessory: _jsx(Button, { variant: "text", size: "tiny", icon: $icon('x'), "aria-label": $t({
                            defaultMessage: 'Remove draft',
                            id: 'f43b1edc2e',
                            description: 'Aria label for the remove draft button',
                        }), onClick: (event) => {
                            event.stopPropagation();
                            void handleDelete(draft);
                        } }), children: _jsx(TextPrimitive, { variant: "small", className: "line-clamp-2", children: truncate(draft.snapshot.text, DRAFT_PREVIEW_MAX_CHARS) }) }, draft.uuid))) }), draftCount > 1 && (_jsxs(_Fragment, { children: [_jsx(DropdownMenu.Separator, {}), _jsx(DropdownMenu.DestructiveItem, { leadingAccessory: $icon('trash'), onConfirm: () => void handleClear(), confirmation: {
                            title: $t({
                                defaultMessage: 'Clear all drafts?',
                                id: '87080f2921',
                                description: 'Title of the confirmation dialog for clearing saved Ask drafts',
                            }),
                            bodyText: $t({
                                defaultMessage: 'This will permanently remove all saved drafts for this thread.',
                                id: '2711d6e46b',
                                description: 'Confirmation message shown before clearing saved Ask drafts',
                            }),
                            confirmButtonTitle: $t({
                                defaultMessage: 'Clear all',
                                id: '4601c499cb',
                                description: 'Confirmation button for clearing saved Ask drafts',
                            }),
                        }, children: _jsx(FormattedMessage, { defaultMessage: "Clear all drafts", id: "e2089a8bb7", description: "Menu action for clearing all saved Ask drafts in the current thread" }) })] }))] }));
}
//# sourceMappingURL=AskInputDraftsControl.js.map